import { useEffect, useState } from "react";
import { Button, Card, Select, Space, Tag, Text, useToast } from "@app/ui";
import { getProductOperators, listOperators, putProductOperators } from "../api/dpp.js";
import { ErrorAlert } from "../errors/index.js";
import { useT } from "../i18n.js";
import { OPERATOR_ROLES } from "../types.js";
import type { EconomicOperator, EffectiveOperator, OperatorRole } from "../types.js";

/** "use the org default" sentinel in the per-role select. */
const DEFAULT = "__default";

/**
 * Per-product operator overrides: for each role, pick one of the org's
 * operators or fall back to the org default. Publish freezes the effective
 * set into the passport.
 */
export function ProductOperatorsCard({
  orgId,
  productId,
  canWrite,
}: {
  orgId: string;
  productId: string;
  canWrite: boolean;
}) {
  const t = useT();
  const toast = useToast();
  const [effective, setEffective] = useState<EffectiveOperator[]>([]);
  const [operators, setOperators] = useState<EconomicOperator[]>([]);
  const [choice, setChoice] = useState<Record<OperatorRole, string>>({} as Record<OperatorRole, string>);
  const [error, setError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setError(null);
    try {
      const [eff, ops] = await Promise.all([getProductOperators(orgId, productId), listOperators(orgId)]);
      setEffective(eff);
      setOperators(ops);
      const next = {} as Record<OperatorRole, string>;
      for (const e of eff) next[e.role] = e.source === "product" && e.operator ? e.operator.id : DEFAULT;
      setChoice(next);
    } catch (e) {
      setError(e);
    }
  };
  useEffect(() => {
    void load();
  }, [orgId, productId]);

  const dirty = effective.some((e) => {
    const current = e.source === "product" && e.operator ? e.operator.id : DEFAULT;
    return (choice[e.role] ?? DEFAULT) !== current;
  });

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const assignments = OPERATOR_ROLES.map((role) => ({
        role,
        operatorId: choice[role] && choice[role] !== DEFAULT ? choice[role]! : null,
      }));
      setEffective(await putProductOperators(orgId, productId, assignments));
      toast.success(t("operators.assigned"));
    } catch (e) {
      setError(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      title={t("operators.productTitle")}
      extra={
        canWrite ? (
          <Button size="sm" variant="primary" loading={saving} disabled={!dirty} onClick={() => void save()}>
            {t("common.save")}
          </Button>
        ) : undefined
      }
    >
      <Space direction="vertical" size="md" style={{ width: "100%" }}>
        <Text small secondary>
          {t("operators.productHint")}
        </Text>
        {error != null && <ErrorAlert error={error} onRetry={() => void load()} />}
        {effective.map((e) => {
          const candidates = operators.filter((o) => o.role === e.role);
          const def = operators.find((o) => o.role === e.role && o.isDefault);
          return (
            <Space key={e.role} wrap>
              <div style={{ width: 300 }}>
                <Select
                  label={t(`operatorRole.${e.role}`)}
                  disabled={!canWrite}
                  options={[
                    {
                      label: def
                        ? `${t("operators.useDefault")} — ${def.legalName}`
                        : t("operators.noneNamed"),
                      value: DEFAULT,
                    },
                    ...candidates.map((o) => ({ label: o.legalName, value: o.id })),
                  ]}
                  value={choice[e.role] ?? DEFAULT}
                  onChange={(v) => setChoice({ ...choice, [e.role]: v })}
                />
              </div>
              <Tag
                color={e.source === "product" ? "primary" : e.source === "default" ? "success" : "default"}
              >
                {t(`operators.source.${e.source}`)}
              </Tag>
            </Space>
          );
        })}
      </Space>
    </Card>
  );
}
